// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { DashMiddleware, DashNext, DashStore } from '@makemydeal/dr-shared-store';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { PurchaseHistoryProxyOutcome } from '@makemydeal/dr-dash-bff-types';
import { buildUniqueScrollRequestId } from '@makemydeal/dr-dash-ui-utils';

// actions
import {
    purchaseHistoryNotificationActivated,
    purchaseHistoryNotificationDismissed,
    purchaseHistoryNotificationRaised,
    purchaseHistoryRequestAccepted,
    purchaseHistoryRequested,
    purchaseHistoryRequestFailed,
    purchaseHistoryRequestUnavailable,
    purchaseHistoryTakingLonger,
    purchaseHistoryTimedOut,
    requestPurchaseHistory
} from '../actions/purchaseHistoryActionCreators';
import { setCustomerInsightsSlideoutStatus } from '../actions/baseDealActionCreators';

// consts/enums
import { SHOW_CUSTOMER_INSIGHTS_SLIDEOUT } from '../actionTypes/baseDealActionTypes';
import {
    PURCHASE_HISTORY_EVENT_RECEIVED,
    PURCHASE_HISTORY_FETCH_FAILURE,
    PURCHASE_HISTORY_FETCH_SUCCESS,
    PURCHASE_HISTORY_NOTIFICATION_ACTIVATED,
    PURCHASE_HISTORY_NOTIFICATION_DISMISSED
} from '../actionTypes/purchaseHistoryActionTypes';

// selectors
import * as baseDealSelectors from '../selectors/BaseDealSelectors';
import * as purchaseHistorySelectors from '../selectors/purchaseHistorySelectors';
import { isDashboardCurrentRoute } from '../selectors/router';

/** Exported so tests advance fake timers by this value rather than a duplicated literal. */
export const PURCHASE_HISTORY_SLOW_HINT_MS = 30_000;

/**
 * One second past the service's 120s in-flight claim, after which an identical request would start a
 * fresh lookup. Not a promise that an answer arrives by then — its lookup budget is far longer — just
 * the point at which the section stops spinning and says so.
 */
export const PURCHASE_HISTORY_TIMEOUT_MS = 121_000;

/**
 * How long the late-result notification stays on screen.
 *
 * Owned here rather than by the Snackbar: Interstate hard-codes `autoHideDuration` to 5 seconds when
 * `autoHide` is set and exposes no prop to change it. Five seconds is short for a notification whose
 * whole premise is that the manager is looking somewhere else, so the component no longer auto-hides
 * and this timer dismisses it instead. **Change this one number to tune it.**
 */
export const PURCHASE_HISTORY_NOTIFICATION_MS = 10_000;

// Module-level ephemeral timers. Not in redux: nothing subscribes to them, and a handle is not state.
let slowHintTimer: ReturnType<typeof setTimeout> | null = null;
let timeoutTimer: ReturnType<typeof setTimeout> | null = null;
let notificationTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Set for exactly one panel-open — the one caused by acting on the late-result notification, which
 * would otherwise re-enter here and re-request, discarding the result just announced. A flag rather
 * than a distinct action type, because the panel genuinely IS being opened.
 */
let suppressRequestForNextOpen = false;

/** Test helper. Call in `beforeEach` or a timer armed by one test fires during another. */
export function __resetPurchaseHistoryState(): void {
    if (slowHintTimer) {
        clearTimeout(slowHintTimer);
    }
    if (timeoutTimer) {
        clearTimeout(timeoutTimer);
    }
    if (notificationTimer) {
        clearTimeout(notificationTimer);
    }
    slowHintTimer = null;
    timeoutTimer = null;
    notificationTimer = null;
    suppressRequestForNextOpen = false;
}

const clearNotificationTimer = () => {
    if (notificationTimer) {
        clearTimeout(notificationTimer);
        notificationTimer = null;
    }
};

const clearClocks = () => {
    if (slowHintTimer) {
        clearTimeout(slowHintTimer);
        slowHintTimer = null;
    }
    if (timeoutTimer) {
        clearTimeout(timeoutTimer);
        timeoutTimer = null;
    }
};

/** Only `accepted` leaves the section loading; everything else means no push will ever arrive. */
const resolutionForOutcome = (outcome?: PurchaseHistoryProxyOutcome) => {
    switch (outcome) {
        case PurchaseHistoryProxyOutcome.ACCEPTED:
            return null;
        // Understood and declined — nothing to retry, unlike a failure which may be transient.
        case PurchaseHistoryProxyOutcome.MALFORMED_REQUEST:
        case PurchaseHistoryProxyOutcome.NO_CUSTOMER_IDENTIFIER:
        case PurchaseHistoryProxyOutcome.NO_DEAL_IDENTIFIER:
        case PurchaseHistoryProxyOutcome.NO_DEALER_IDENTIFIER:
        case PurchaseHistoryProxyOutcome.DOWNSTREAM_REFUSED:
            return purchaseHistoryRequestUnavailable();
        default:
            // A downstream failure or timeout, or an outcome this build does not recognise. Treated
            // as a failure rather than silently left loading.
            return purchaseHistoryRequestFailed();
    }
};

/** Whether the manager can currently see the section. */
const isSectionVisible = (state: any): boolean =>
    Boolean(baseDealSelectors.getCustomerInsightsSlideoutStatus(state)) && Boolean(isDashboardCurrentRoute(state));

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    next(action);

    switch (action.type) {
        case SHOW_CUSTOMER_INSIGHTS_SLIDEOUT: {
            if (!action.payload?.showCustomerInsightsSlideout) {
                // Closing does not cancel the lookup — that is what the late-result notification is for.
                break;
            }

            if (suppressRequestForNextOpen) {
                // Reopened by the notification: the result is already in the slice.
                suppressRequestForNextOpen = false;
                break;
            }

            /*
             * Every open asks. Duplicate suppression is the SERVICE's job, not this middleware's: its
             * claim store recognises an identical lookup already in flight and answers 202 without
             * starting a second one, and the original lookup's push still resolves the section.
             */
            store.dispatch(purchaseHistoryRequested(Date.now()));
            store.dispatch(requestPurchaseHistory(store.getState()) as unknown as AnyFSA);

            logNewRelicPageAction('MV:PurchaseHistory:Requested', {});

            clearClocks();

            slowHintTimer = setTimeout(() => {
                slowHintTimer = null;
                // Re-read state: a timer that survived a race must not act on the state that armed it.
                if (purchaseHistorySelectors.getIsPurchaseHistoryRequestOutstanding(store.getState())) {
                    store.dispatch(purchaseHistoryTakingLonger());
                    logNewRelicPageAction('MV:PurchaseHistory:SlowHintShown', {});
                }
            }, PURCHASE_HISTORY_SLOW_HINT_MS);

            timeoutTimer = setTimeout(() => {
                timeoutTimer = null;
                if (purchaseHistorySelectors.getIsPurchaseHistoryRequestOutstanding(store.getState())) {
                    store.dispatch(purchaseHistoryTimedOut());
                    logNewRelicPageAction('MV:PurchaseHistory:TimedOut', {});
                    logNewRelicPageAction('MV:PurchaseHistory:Resolved', {
                        finalState: 'unavailable',
                        recordCount: 0,
                        requestToRenderDelayMs: null
                    });
                }
                clearClocks();
            }, PURCHASE_HISTORY_TIMEOUT_MS);

            break;
        }

        case PURCHASE_HISTORY_FETCH_SUCCESS: {
            const outcome = action.payload?.outcome as PurchaseHistoryProxyOutcome | undefined;
            const resolution = resolutionForOutcome(outcome);

            if (!resolution) {
                // `payload` provably exists here: reaching this branch required reading `outcome` from it.
                store.dispatch(purchaseHistoryRequestAccepted(action.payload.correlationId ?? null));
                break;
            }

            clearClocks();
            store.dispatch(resolution);
            logNewRelicPageAction('MV:PurchaseHistory:Resolved', {
                finalState: 'unavailable',
                outcome: outcome ?? 'unknown',
                recordCount: 0,
                requestToRenderDelayMs: null
            });
            break;
        }

        case PURCHASE_HISTORY_FETCH_FAILURE: {
            clearClocks();
            store.dispatch(purchaseHistoryRequestFailed());
            logNewRelicPageAction('MV:PurchaseHistory:Resolved', {
                finalState: 'unavailable',
                outcome: 'request_failed',
                recordCount: 0,
                requestToRenderDelayMs: null
            });
            break;
        }

        case PURCHASE_HISTORY_EVENT_RECEIVED: {
            // The push passed the handler's guards, so the section is resolved either way.
            clearClocks();

            const state = store.getState();
            const slice = purchaseHistorySelectors.getPurchaseHistory(state);
            const requestedAtMs = slice.requestedAtMs;

            logNewRelicPageAction('MV:PurchaseHistory:Resolved', {
                finalState: slice.status,
                recordCount: slice.records.length,
                requestToRenderDelayMs: requestedAtMs === null ? null : Date.now() - requestedAtMs
            });

            // A failed lookup is not worth interrupting for — there is nothing to come back to.
            const isResult = slice.status === 'ready' || slice.status === 'empty';
            if (isResult && !isSectionVisible(state)) {
                store.dispatch(purchaseHistoryNotificationRaised());
                logNewRelicPageAction('MV:PurchaseHistory:NotificationRaised', { finalState: slice.status });

                clearNotificationTimer();
                notificationTimer = setTimeout(() => {
                    notificationTimer = null;
                    // Re-read: the manager may have acted on or dismissed it in the meantime.
                    if (purchaseHistorySelectors.getIsPurchaseHistoryNotificationRaised(store.getState())) {
                        store.dispatch(purchaseHistoryNotificationDismissed());
                    }
                }, PURCHASE_HISTORY_NOTIFICATION_MS);
            }
            break;
        }

        case PURCHASE_HISTORY_NOTIFICATION_DISMISSED: {
            clearNotificationTimer();
            break;
        }

        case PURCHASE_HISTORY_NOTIFICATION_ACTIVATED: {
            clearNotificationTimer();
            // Reopen only: a fresh request would discard the result just announced.
            suppressRequestForNextOpen = true;
            store.dispatch(setCustomerInsightsSlideoutStatus(true));
            logNewRelicPageAction('MV:PurchaseHistory:NotificationActivated', {});
            break;
        }

        default:
            break;
    }
};

/** Stamps a fresh scroll request id, so an already-mounted section is still scrolled to. */
export const activatePurchaseHistoryNotification = () =>
    purchaseHistoryNotificationActivated(buildUniqueScrollRequestId('purchase-history'));
